import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useParams } from 'react-router-dom';

interface DemoData {
  individual: any;
  values: any[];
  aspirations: any[];
  goals: any[];
  commitments: any[];
  habits: any[];
  connections: any[];
  entries: any[];
  moments: any[];
}

interface DemoContextType {
  isDemoMode: boolean;
  demoUserId: string | null;
  demoData: DemoData | null;
  isLoading: boolean;
  getDemoPath: (path: string) => string;
}

const DemoContext = createContext<DemoContextType>({
  isDemoMode: false,
  demoUserId: null,
  demoData: null,
  isLoading: false,
  getDemoPath: (path: string) => path, // Default: no prefix
});

export function useDemoContext() {
  return useContext(DemoContext);
}

interface DemoProviderProps {
  children: ReactNode;
}

export function DemoProvider({ children }: DemoProviderProps) {
  const { profileId } = useParams();
  const [demoData, setDemoData] = useState<DemoData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (profileId) {
      setIsLoading(true);
      
      // TODO: Load demo data from Firestore demo database
      // For now, use the API to fetch it
      const DEMO_API = 'https://demo-seeder-77pyygjmhq-uc.a.run.app';
      
      fetch(`${DEMO_API}/demo/validate/${profileId}`)
        .then(res => res.json())
        .then(data => {
          if (data.valid) {
            // Data is in Firestore, now we need to fetch it
            // For MVP, we'll load from the JSON files
            loadDemoDataFromFiles(profileId).then(setDemoData);
          }
        })
        .catch(err => {
          console.error('Failed to load demo data:', err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [profileId]);

  const getDemoPath = (path: string) => {
    if (!profileId) return path;
    // Ensure path starts with /
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `/demo/${profileId}${normalizedPath}`;
  };

  return (
    <DemoContext.Provider
      value={{
        isDemoMode: !!profileId,
        demoUserId: profileId || null,
        demoData,
        isLoading,
        getDemoPath,
      }}
    >
      {children}
    </DemoContext.Provider>
  );
}

// Helper to load demo data from JSON files (temporary until we connect to Firestore)
async function loadDemoDataFromFiles(userId: string): Promise<DemoData | null> {
  try {
    const base = `/demo-data/${userId}`;
    
    const [
      individual,
      values,
      aspirations,
      goals,
      commitments,
      habits,
      connections,
      entries,
      moments,
    ] = await Promise.all([
      fetch(`${base}/individual.json`).then(r => r.json()),
      fetch(`${base}/values.json`).then(r => r.json()),
      fetch(`${base}/aspirations.json`).then(r => r.json()),
      fetch(`${base}/goals.json`).then(r => r.json()),
      fetch(`${base}/commitments.json`).then(r => r.json()),
      fetch(`${base}/habits.json`).then(r => r.json()),
      fetch(`${base}/connections.json`).then(r => r.json()),
      fetch(`${base}/entries.json`).then(r => r.json()),
      fetch(`${base}/moments.json`).then(r => r.json()),
    ]);

    return {
      individual,
      values,
      aspirations,
      goals,
      commitments,
      habits,
      connections,
      entries,
      moments,
    };
  } catch (error) {
    console.error('Failed to load demo data files:', error);
    return null;
  }
}

